import { Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  IFieldFieldsFragment,
  IMeasureFieldsFragment,
  IFunnelStepBriefInfoFieldsFragment,
  IUserAliasReplacementFieldsFragment,
  MetricTypeKey,
  QueryType,
  SignificanceModeKey,
  SignificanceTestKey,
  FunnelModeKey,
  IFunnelModeSliceFieldsFragment,
  FunnelTimeToConvertMetric,
} from '../../../../utils/apollo/graphqlSchema';
import { IBuilder } from '../types';
import { assertCondition } from '../../../../utils/types';
import {
  defaultBreakdownCriteria,
  defaultCriteriaBySignificanceMode,
  defaultExperimentCriteria,
  defaultMetricBySignificanceMetricType,
} from './defaults';
import {
  ISignificanceBuilderBreakdownCriteria,
  ISignificanceBuilderExperimentCriteria,
  ISignificanceBuilderSegmentCriteria,
  ISignificanceErrors,
} from './types';

export const setSignificanceModeReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newMode: SignificanceModeKey;
  }>,
) => {
  const { newMode } = action.payload;
  const significance = draft.significance;
  const errors = draft.significance.errors;
  const selectedOptions = draft.selectedOptions;
  assertCondition(selectedOptions?.type === QueryType.Significance);

  selectedOptions.mode = newMode;

  // Mode change will reset criteria to default
  if (significance.criteria) {
    significance.criteria = defaultCriteriaBySignificanceMode[newMode];
  }

  // reset criteria errors
  if (errors?.criteriaField) {
    delete errors.criteriaField;
  }
  if (errors?.controlGroup) {
    delete errors.controlGroup;
  }
  if (errors?.criteriaVariant) {
    delete errors.criteriaVariant;
  }
};

export const setSignificanceMetricTypeReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newType: MetricTypeKey;
  }>,
) => {
  const { newType } = action.payload;
  const significance = draft.significance;
  const errors = draft.significance.errors;
  const selectedOptions = draft.selectedOptions;
  assertCondition(selectedOptions?.type === QueryType.Significance);

  selectedOptions.metric = newType;

  // Type change will reset metric to default
  if (significance.metric) {
    significance.metric = defaultMetricBySignificanceMetricType[newType];
  }

  // reset metric errors
  if (errors?.metricMeasure) {
    delete errors.metricMeasure;
  }
  if (errors?.metricAnalysis) {
    delete errors.metricAnalysis;
  }
};
export const setSignificanceMeasureMetricReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    namedMeasure: IMeasureFieldsFragment | null;
  }>,
) => {
  const { namedMeasure } = action.payload;
  const errors = draft.significance.errors;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Measure);

  draft.significance.metric.measure = namedMeasure;

  if (errors?.metricMeasure) {
    delete errors.metricMeasure;
  }
};
export const setSignificanceFunnelReportReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    analysisId: string | null;
    steps: IFunnelStepBriefInfoFieldsFragment[];
    aliasReplacements?: IUserAliasReplacementFieldsFragment[];
    mode: FunnelModeKey | null;
    modeSlices: IFunnelModeSliceFieldsFragment[];
  }>,
) => {
  const { analysisId, steps, aliasReplacements, mode, modeSlices } =
    action.payload;
  const errors = draft.significance.errors;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Funnel);

  draft.significance.metric.analysisId = analysisId;
  draft.significance.metric.steps = steps;

  if (aliasReplacements) {
    draft.significance.metric.aliasReplacements = aliasReplacements;
  }

  draft.significance.metric.mode = mode;

  if (modeSlices) {
    draft.significance.metric.modeSlices = modeSlices;
  }

  if (errors?.metricAnalysis) {
    delete errors.metricAnalysis;
  }
};
export const setSignificanceFunnelStepReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    stepIndex: number;
  }>,
) => {
  const { stepIndex } = action.payload;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Funnel);

  draft.significance.metric.convertedOnStep = stepIndex;
};

export const setSignificanceFunnelTimeToConvertMetricReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    ttcMetric: FunnelTimeToConvertMetric | null;
  }>,
) => {
  const { ttcMetric } = action.payload;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Funnel);

  draft.significance.metric.ttcMetric = ttcMetric;
};

export const setSignificanceQueryReportReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    analysisId: string | null;
    measureId: string | null;
    aliasReplacements?: IUserAliasReplacementFieldsFragment[];
  }>,
) => {
  const { analysisId, measureId, aliasReplacements } = action.payload;
  const errors = draft.significance.errors;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Query);

  draft.significance.metric.analysisId = analysisId;
  draft.significance.metric.measureId = measureId ?? null;
  if (aliasReplacements) {
    draft.significance.metric.aliasReplacements = aliasReplacements;
  }
  if (errors?.metricAnalysis) {
    delete errors.metricAnalysis;
  }
};
export const setSignificanceRetentionReportReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    analysisId: string | null;
  }>,
) => {
  const { analysisId } = action.payload;
  const errors = draft.significance.errors;

  assertCondition(draft.significance.metric?.type === MetricTypeKey.Retention);

  draft.significance.metric.analysisId = analysisId;

  if (errors?.metricAnalysis) {
    delete errors.metricAnalysis;
  }
};
export const setSignificanceTestTypeReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newTestKey: SignificanceTestKey;
  }>,
) => {
  const { newTestKey } = action.payload;
  const selectedOptions = draft.selectedOptions;
  assertCondition(selectedOptions?.type === QueryType.Significance);

  selectedOptions.test = newTestKey;
};
export const setSignificanceLevelReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newValue: number;
  }>,
) => {
  const { newValue } = action.payload;
  const significance = draft.significance;

  significance.significanceLevel = newValue;
};
export const setSignificanceConfidenceLevelReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newValue: number;
  }>,
) => {
  const { newValue } = action.payload;
  const significance = draft.significance;

  significance.confidenceLevel = newValue;
};
export const setSignificanceBreakdownFieldReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    newField: IFieldFieldsFragment | null;
  }>,
) => {
  const { newField } = action.payload;
  const selectedOptions = draft.selectedOptions;
  const errors = draft.significance.errors;

  assertCondition(
    selectedOptions?.type === QueryType.Significance &&
      selectedOptions.mode === SignificanceModeKey.Breakdown,
  );

  // Field change will reset Breakdown criteria
  draft.significance.criteria = {
    ...defaultBreakdownCriteria,
    field: newField,
  };

  // reset errors
  if (errors?.criteriaField) {
    delete errors.criteriaField;
  }
  if (errors?.controlGroup) {
    delete errors.controlGroup;
  }
  if (errors?.criteriaVariant) {
    delete errors.criteriaVariant;
  }
};

export const removeSignificanceCriteriaVariantReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    variantIndex: number;
  }>,
) => {
  const { variantIndex } = action.payload;

  assertCondition(draft.significance.criteria);

  draft.significance.criteria.variants.splice(variantIndex, 1);
};

export const setSignificanceErrorsReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    errors: ISignificanceErrors;
  }>,
) => {
  const { errors } = action.payload;

  draft.significance.errors = errors;
};

export const setSignificanceExperimentIdReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<{
    id: string | null;
  }>,
) => {
  const { id } = action.payload;
  const errors = draft.significance.errors;

  // Experiment ID change will reset Experiment criteria
  draft.significance.criteria = {
    ...defaultExperimentCriteria,
    experimentId: id,
  };

  if (errors?.criteriaField) {
    delete errors.criteriaField;
  }
};

export const setSignificanceControlGroupReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<
    | Pick<ISignificanceBuilderBreakdownCriteria, 'modeKey' | 'controlGroup'>
    | Pick<ISignificanceBuilderSegmentCriteria, 'modeKey' | 'controlGroup'>
    | Pick<ISignificanceBuilderExperimentCriteria, 'modeKey' | 'controlGroup'>
  >,
) => {
  const { modeKey, controlGroup } = action.payload;
  const errors = draft.significance.errors;

  assertCondition(
    draft.significance.criteria &&
      draft.significance.criteria.modeKey === modeKey,
  );

  draft.significance.criteria.controlGroup = controlGroup;

  // reset error
  if (errors?.controlGroup) {
    delete errors.controlGroup;
  }
};
export const setSignificanceVariantReducer = (
  draft: Draft<IBuilder>,
  action: PayloadAction<
    | {
        modeKey: SignificanceModeKey.Breakdown;
        variant: ISignificanceBuilderBreakdownCriteria['variants'][0];
        variantIndex: number;
      }
    | {
        modeKey: SignificanceModeKey.Segment;
        variant: ISignificanceBuilderSegmentCriteria['variants'][0];
        variantIndex: number;
      }
    | {
        modeKey: SignificanceModeKey.Experiment;
        variant: ISignificanceBuilderExperimentCriteria['variants'][0];
        variantIndex: number;
      }
  >,
) => {
  const { modeKey, variant, variantIndex } = action.payload;
  const errors = draft.significance.errors;

  assertCondition(
    draft.significance.criteria &&
      draft.significance.criteria.modeKey === modeKey,
  );

  draft.significance.criteria.variants[variantIndex] = variant;

  // reset error
  if (errors?.criteriaVariant) {
    delete errors.criteriaVariant;
  }
};
